import React, { useState, useEffect, useRef } from 'react';
import { useCanvas } from '../../../hooks/useCanvas';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts';
import { Maximize, ZoomIn, ZoomOut, Magnet } from 'lucide-react';

const TimelineControls = ({ duration, onZoomChange, snapEnabled, onSnapToggle }) => {
  const { togglePlayback, setTimelineZoom } = useCanvas();

  // Use duration as the base zoom level (1x = timeline fits the container)
  const [currentZoom, setCurrentZoom] = useState(duration || 100);
  const ZOOM_STEP = 0.2; // 20% increment/decrement per zoom action
  const MAX_ZOOM_MULTIPLIER = 3; // 3x duration for maximum zoom-in
  const MIN_ZOOM_MULTIPLIER = 0.5; // 0.5x duration for maximum zoom-out

  // Track if this is the initial mount to prevent unnecessary resets
  const isInitialMount = useRef(true);
  const prevDuration = useRef(duration);

  // Only reset zoom on initial mount or maintain zoom ratio when duration changes
  useEffect(() => {
    // On initial mount, set zoom to fit
    if (isInitialMount.current) {
      isInitialMount.current = false;
      onZoomChange?.(duration || 100);
      return;
    }

    // When duration changes (e.g., track resized), maintain the zoom ratio
    if (prevDuration.current !== duration && duration > 0 && prevDuration.current > 0) {
      // Calculate the current zoom ratio (how zoomed in/out we are relative to fit)
      const currentZoomRatio = currentZoom / prevDuration.current;

      // Apply the same zoom ratio to the new duration
      const newZoom = duration * currentZoomRatio;

      // Clamp to min/max bounds based on new duration
      const clampedZoom = Math.max(
        duration * MIN_ZOOM_MULTIPLIER,
        Math.min(newZoom, duration * MAX_ZOOM_MULTIPLIER)
      );

      setCurrentZoom(clampedZoom);
      if (setTimelineZoom) setTimelineZoom(clampedZoom);
      onZoomChange?.(clampedZoom);
      prevDuration.current = duration;
    }
  }, [duration]);

  // Separate effect to notify parent of zoom changes (user-initiated only)
  const notifyZoomChange = (newZoom) => {
    setCurrentZoom(newZoom);
    if (setTimelineZoom) setTimelineZoom(newZoom);
    if (onZoomChange) onZoomChange(newZoom);
  };

  const shortcutCallbacks = {
    PLAY_PAUSE: () => {
      togglePlayback();
    },
  };

  useKeyboardShortcuts(undefined, shortcutCallbacks);

  const handleFitZoom = () => {
    const fitZoom = duration || 100;
    notifyZoomChange(fitZoom);
  };

  const handleZoomIn = () => {
    const MAX_ZOOM = duration * MAX_ZOOM_MULTIPLIER;
    const newZoom = Math.min(currentZoom * (1 + ZOOM_STEP), MAX_ZOOM);
    notifyZoomChange(newZoom);
  };

  const handleZoomOut = () => {
    const MIN_ZOOM = duration * MIN_ZOOM_MULTIPLIER;
    const newZoom = Math.max(currentZoom * (1 - ZOOM_STEP), MIN_ZOOM);
    notifyZoomChange(newZoom);
  };

  // Check if zoom buttons should be disabled
  const MAX_ZOOM = duration * MAX_ZOOM_MULTIPLIER;
  const MIN_ZOOM = duration * MIN_ZOOM_MULTIPLIER;
  const isZoomInDisabled = currentZoom >= MAX_ZOOM;
  const isZoomOutDisabled = currentZoom <= MIN_ZOOM;

  return (
    <div className="flex items-center space-x-3">
      {/* Snap Toggle Button */}
      <button
        onClick={onSnapToggle}
        className={`p-1 rounded transition-colors ${snapEnabled
          ? 'bg-ag-green text-white hover:bg-ag-green/80'
          : 'text-gray-300 hover:bg-ag-black-800'
          }`}
        title={snapEnabled ? 'Snapping: ON (Click to disable)' : 'Snapping: OFF (Click to enable)'}
      >
        <Magnet size={20} />
      </button>

      {/* Zoom Out Button */}
      <button
        onClick={handleZoomOut}
        disabled={isZoomOutDisabled}
        className={`p-1 rounded transition-colors ${isZoomOutDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-ag-black-800'
          }`}
        title="Zoom Out"
      >
        <ZoomOut size={20} className="text-gray-300" />
      </button>

      {/* Zoom In Button */}
      <button
        onClick={handleZoomIn}
        disabled={isZoomInDisabled}
        className={`p-1 rounded transition-colors ${isZoomInDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-ag-black-800'
          }`}
        title="Zoom In"
      >
        <ZoomIn size={20} className="text-gray-300" />
      </button>

      {/* Fit Zoom Button */}
      <button
        onClick={handleFitZoom}
        className="p-1 hover:bg-ag-black-800 rounded transition-colors"
        title="Fit Zoom"
      >
        <Maximize size={20} className="text-gray-300" />
      </button>
    </div>
  );
};

export default TimelineControls;